import type { SetManyUserData } from "@exonn/js-sdk";
import { logger } from "src/logger";
import { useExonnApi } from "src/exonn-api/exonn-api.tsx";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useDebouncedCallback } from "@exonn/hooks";
import { listenUserUnauthenticated } from "src/state/events.ts";
import type { StorageKey } from "@exonn/storage";
export type UserDataItem<Value = any> = {
  key: StorageKey;
  value: Value | null;
};

export const UserDataApiContext = createContext<null | {
  setUserData: <Value = any>(item: UserDataItem<Value>) => void;
  forceSavePendingUserData: () => Promise<any>;
}>(null);

type UserDataState = {
  pendingUserData: {
    name: string;
    payload: string;
    version: string;
    timestamp: string;
  }[];
};

export const UserDataProvider = ({ children }: PropsWithChildren) => {
  const api = useExonnApi();
  const state = useRef<UserDataState>({
    pendingUserData: [],
  });

  const [debouncedManySetUserData, cancelDebouncedManySetUserData] =
    useDebouncedCallback(
      async (params, data: SetManyUserData.RequestBody) => {
        logger.debug("saving user state to database", {
          data,
        });
        const response = await api.userData.setManyUserData(params, data);

        // response.data.data.forEach((item) => {
        //   const localKey = getUserDataStorageKey({
        //     user: user!,
        //     key: {
        //       name: item.name,
        //       version: item.version,
        //     },
        //   });
        //   console.log("localKey", localKey);
        //   const local = localStorageDriver.getFull(localKey);
        //
        //   if (local) {
        //     if (local.timestamp <= +item.timestamp) {
        //       console.log("local value is older than db value");
        //       localStorageDriver.remove(localKey);
        //     }
        //   }
        //   console.log("local", local);
        //   console.log("db", item);
        // });
        return response.data;
      },
      1500,
      () => {
        state.current.pendingUserData = [];
      },
    );

  const forceSavePendingUserData = useCallback(async () => {
    if (!state.current.pendingUserData.length) {
      return;
    }
    const data = {
      items: [...state.current.pendingUserData],
    };
    cancelDebouncedManySetUserData();
    logger.debug("saving user state to database", {
      data,
    });
    const response = await api.userData.setManyUserData(undefined, data);

    state.current.pendingUserData = [];
    return response.data;
  }, [api.userData, cancelDebouncedManySetUserData]);

  useEffect(() => {
    return listenUserUnauthenticated(() => {
      // it makes no sense to send request to save user data after user logged out
      // because this endpoint is protected from unauthorized access
      cancelDebouncedManySetUserData();
    });
  }, [cancelDebouncedManySetUserData]);

  const setUserDataBatched = useCallback(
    (item: UserDataItem) => {
      const existingItemIndex = state.current.pendingUserData.findIndex(
        (innerItem) => innerItem.name === item.key.name,
      );

      if (existingItemIndex !== -1) {
        state.current.pendingUserData[existingItemIndex] = {
          payload: JSON.stringify(item.value),
          name: item.key.name,
          version: item.key.version,
          timestamp: Date.now() + "",
        };
      } else {
        state.current.pendingUserData.push({
          payload: JSON.stringify(item.value),
          name: item.key.name,
          version: item.key.version,
          timestamp: Date.now() + "",
        });
      }

      // todo: log error
      return debouncedManySetUserData(null, {
        items: [...state.current.pendingUserData],
      });
    },
    [debouncedManySetUserData],
  );

  const userDataApi = useMemo(
    () => ({
      setUserData: setUserDataBatched,
      forceSavePendingUserData,
    }),
    [setUserDataBatched, forceSavePendingUserData],
  );

  return (
    <UserDataApiContext.Provider value={userDataApi}>
      {children}
    </UserDataApiContext.Provider>
  );
};
